import React, { Fragment, FunctionComponent } from 'react';
import { List, Collapse, ListItem, ListItemText, FormControlLabel, Radio, Checkbox, FormControl, InputLabel, Select, MenuItem, Grid, FormHelperText, Input, Box, Typography, Tooltip } from '@material-ui/core';
import styles from './ReferralService.Styles';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import InfoOutlined from '@material-ui/icons/InfoOutlined';
import ServiceDataModel from '../../../../data-models/Service.DataModel';
import { ServicesEnum } from '../../../../support/enums/Services.Enum';
import { toast } from 'react-toastify';
import FacilityTypeEntity from '../../../../entities/FacilityType.Entity';
import { SIU_SERVICE_TYPE_DESCRIPTIONS } from './SiuServiceTypeDescriptions';

interface IReferralServiceListProps {
    service: ServiceDataModel;
    handleServiceTypesSelected: (serviceTypeSelected: number[]) => void;
    expanded?: boolean;
    serviceSelected: number;
    setServiceSelected: (serviceSelected: number) => void;
    serviceTypesSelected: Array<number>;
    selectedFacilityTypeIds: number[];
    handleFacilityTypeChange: (facilityTypeIds: number[]) => void;
    facilityTypes: FacilityTypeEntity[];
    showFacilityTypeError?: boolean;
    eRemoteUnmannedNumberOfDays?: number | null;
    surveillanceMannedNumberOfDays?: number | null;
    handleSiuNumberOfDaysChange?: (serviceTypeId: number, value: number | null) => void;
    showSiuNumberOfDaysError?: boolean;
    backgroundInvestigationOption?: string | null;
    eRemoteUnmannedOption?: string | null;
    handleSiuOptionChange?: (serviceTypeId: number, value: string | null) => void;
    showSiuOptionError?: boolean;
    medicalCanvassNumberOfFacilities?: number | null;
}

export const ReferralServiceList: FunctionComponent<IReferralServiceListProps> = ({
    serviceSelected,
    setServiceSelected,
    service,
    handleServiceTypesSelected,
    expanded,
    serviceTypesSelected,
    selectedFacilityTypeIds,
    handleFacilityTypeChange,
    facilityTypes,
    showFacilityTypeError = false,
    eRemoteUnmannedNumberOfDays = null,
    surveillanceMannedNumberOfDays = null,
    handleSiuNumberOfDaysChange,
    showSiuNumberOfDaysError = false,
    backgroundInvestigationOption = null,
    eRemoteUnmannedOption = null,
    handleSiuOptionChange,
    showSiuOptionError = false,
    medicalCanvassNumberOfFacilities = null,
}: IReferralServiceListProps) => {
    const classes = styles();
    const [openServiceE, setOpenService] = React.useState(expanded ? expanded : false);

    const MEDICAL_CANVASS_SERVICE_TYPE_ID = 69;
    const EREMOTE_UNMANNED_SERVICE_TYPE_ID = 67;
    const SURVEILLANCE_MANNED_SERVICE_TYPE_ID = 73;
    const BACKGROUND_INVESTIGATION_SERVICE_TYPE_ID = 64;

    const BACKGROUND_INVESTIGATION_OPTIONS = ['Standard', 'Traditional', 'Extended'];
    const EREMOTE_OPTIONS = ['HD Live', 'HD Stream'];

    const sortedServiceTypes = [...service.serviceTypes].sort((a, b) => {
        if (a.orderToDisplay === null && b.orderToDisplay === null) return 0;
        if (a.orderToDisplay === null) return 1;
        if (b.orderToDisplay === null) return -1;
        return a.orderToDisplay - b.orderToDisplay;
    });
    
    const handleClick = () => {
        setOpenService(!openServiceE);
    };

    const getValidServiceTypeIdsByService = (serviceTypeIds: number[], selectedService: ServiceDataModel): number[] => {
        if (serviceTypeIds.length === 0) return [];
    
        if (!selectedService) return [];
    
        const allServiceTypesByService = selectedService.serviceTypes;
        const serviceTypeIdsSet = new Set(allServiceTypesByService.map(st => st.serviceTypeId));
    
        return serviceTypeIds.filter(id => serviceTypeIdsSet.has(id));
    };

    const myHandleServiceTypeSelected = (serviceTypeSelected: number, serviceId: number) => {
        const newArrayOfServiceTypesNotDiagnostic = [];        
        if (!isNaN(serviceTypeSelected)) {
            setServiceSelected(serviceId);
            newArrayOfServiceTypesNotDiagnostic.push(serviceTypeSelected);
            handleServiceTypesSelected(newArrayOfServiceTypesNotDiagnostic);
        }
    };

    const handleMultipleServiceCheckbox = (serviceTypeSelected: number, serviceId: number) => {                
        let newArrayOfServiceTypes: Array<number> = [...serviceTypesSelected];
        const indexOfSelected = newArrayOfServiceTypes.indexOf(serviceTypeSelected);

        if (serviceId !== serviceSelected) {
            newArrayOfServiceTypes = [];
            setServiceSelected(serviceId);
        } else {
            // Clean service types that do not belong to the selected service
            newArrayOfServiceTypes = getValidServiceTypeIdsByService(newArrayOfServiceTypes, service);
        }

        if (indexOfSelected !== -1 && newArrayOfServiceTypes.length > 0) {
            newArrayOfServiceTypes.splice(indexOfSelected, 1);
            // If Medical Canvass is deselected, clear facilityType
            if (serviceTypeSelected === MEDICAL_CANVASS_SERVICE_TYPE_ID) {
                handleFacilityTypeChange([]);
            }
            handleServiceTypesSelected(newArrayOfServiceTypes);
        } else if (indexOfSelected === -1 && !isNaN(serviceTypeSelected)) {
            if (serviceId === 6 && newArrayOfServiceTypes.length === 2) {
                toast.warning('⚠️ ' + 'you only can select up to 2 service types');
            } else if (newArrayOfServiceTypes.length <= 2 || serviceId == ServicesEnum.SIU) {
                newArrayOfServiceTypes.push(serviceTypeSelected);
                handleServiceTypesSelected(newArrayOfServiceTypes);
            } else {
                toast.warning('⚠️ ' + 'you only can select up to 3 services');
            }
        }
    };

    const getTitle = () => (
        <>
            <span className={classes.listItemTitle}>{service.service.serviceName}</span>
            <span className={classes.subtitleDiagnostic}>
                {openServiceE && service.service.serviceId === ServicesEnum.Diagnostic && 'Select up to 3'}
            </span>
        </>
    );

    return (
        <>
            {service.serviceTypes.length > 0 ? (
                <>
                    <ListItem className={classes.serviceTitle} button onClick={handleClick}>
                        {openServiceE ? <ExpandLess /> : <ExpandMore />}
                        <ListItemText primary={getTitle()} />
                    </ListItem>
                    <Collapse in={openServiceE} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {service.service.serviceId === ServicesEnum.Diagnostic ||
                            service.service.serviceId === ServicesEnum.TT || service.service.serviceId === ServicesEnum.SIU
                                ? sortedServiceTypes.map((servicetype, sindex) => {
                                      const isMedicalCanvass = servicetype.serviceTypeId === MEDICAL_CANVASS_SERVICE_TYPE_ID;
                                      const isERemoteUnmanned = servicetype.serviceTypeId === EREMOTE_UNMANNED_SERVICE_TYPE_ID;
                                      const isSurveillanceManned = servicetype.serviceTypeId === SURVEILLANCE_MANNED_SERVICE_TYPE_ID;
                                      const isBackgroundInvestigation = servicetype.serviceTypeId === BACKGROUND_INVESTIGATION_SERVICE_TYPE_ID;
                                      const isSelected = serviceTypesSelected.includes(servicetype.serviceTypeId);
                                      const showDropdown = isMedicalCanvass && isSelected;
                                      const showBackgroundInvestigationDropdown = isBackgroundInvestigation && isSelected;
                                      const showERemoteOptionDropdown = isERemoteUnmanned && isSelected;
                                      const showNumberOfDaysInput =
                                          service.service.serviceId === ServicesEnum.SIU &&
                                          (isERemoteUnmanned || isSurveillanceManned) &&
                                          isSelected;
                                      const numberOfDaysMin = isERemoteUnmanned ? 3 : 1;
                                      const numberOfDaysValue = isERemoteUnmanned
                                          ? eRemoteUnmannedNumberOfDays
                                          : surveillanceMannedNumberOfDays;
                                      const showNumberOfDaysError =
                                          showSiuNumberOfDaysError &&
                                          showNumberOfDaysInput &&
                                          (!numberOfDaysValue || numberOfDaysValue < numberOfDaysMin);
                                      const showRightContent = showDropdown || showNumberOfDaysInput || showBackgroundInvestigationDropdown || showERemoteOptionDropdown;
                                      const numberOfDaysLabelClassName = showNumberOfDaysError
                                          ? classes.numberOfDaysLabel + ' ' + classes.numberOfDaysLabelError
                                          : classes.numberOfDaysLabel;
                                      const showBackgroundInvestigationOptionError =
                                          showSiuOptionError && showBackgroundInvestigationDropdown && !backgroundInvestigationOption;
                                      const showERemoteOptionError =
                                          showSiuOptionError && showERemoteOptionDropdown && !eRemoteUnmannedOption;
                                      const showMedicalCanvassNumberOfFacilitiesError =
                                          showSiuNumberOfDaysError &&
                                          showDropdown &&
                                          (!medicalCanvassNumberOfFacilities || medicalCanvassNumberOfFacilities < 10);

                                      const facilityTypeHasError = showFacilityTypeError && (!selectedFacilityTypeIds || selectedFacilityTypeIds.length === 0);

                                      return (
                                          <ListItem key={servicetype.serviceTypeId} className={classes.nested} style={{ paddingBottom: showRightContent ? 20 : 0 }}>
                                              <Grid container spacing={1} alignItems="flex-end">
                                                  <Grid item xs={showRightContent ? 5 : 12}>
                                                      <ListItemText>
                                                          <FormControlLabel
                                                              control={
                                                                  <Checkbox
                                                                      className={classes.radioButton}
                                                                      checked={
                                                                          serviceTypesSelected.findIndex(
                                                                              (sts) => sts === servicetype.serviceTypeId,
                                                                          ) !== -1
                                                                      }
                                                                      onChange={(event) => {
                                                                          handleMultipleServiceCheckbox(
                                                                              +(event.target as HTMLInputElement).value,
                                                                              servicetype.serviceId,
                                                                          );
                                                                      }}
                                                                      value={servicetype.serviceTypeId}
                                                                  />
                                                              }
                                                              label={
                                                                  <span className={classes.radioButtonLabel}>
                                                                      {servicetype.serviceTypeName}
                                                                      {service.service.serviceId === ServicesEnum.SIU && SIU_SERVICE_TYPE_DESCRIPTIONS[servicetype.serviceTypeName] && (
                                                                          <Tooltip
                                                                              title={SIU_SERVICE_TYPE_DESCRIPTIONS[servicetype.serviceTypeName]}
                                                                              arrow
                                                                              placement="right"
                                                                              classes={{ tooltip: classes.siuTooltip, arrow: classes.siuTooltipArrow }}
                                                                          >
                                                                              <InfoOutlined className={classes.siuInfoIcon} />
                                                                          </Tooltip>
                                                                      )}
                                                                  </span>
                                                              }
                                                          />
                                                      </ListItemText>
                                                  </Grid>
                                                  {showDropdown && (
                                                      <Grid item xs={4}>
                                                          <FormControl 
                                                              fullWidth 
                                                              required
                                                              error={facilityTypeHasError}
                                                              size="small"
                                                              className={classes.formControlRelative}
                                                          >
                                                              <InputLabel
                                                                  id="facility-type-label"
                                                                  className={classes.selectOptionLabel}
                                                                  error={facilityTypeHasError}
                                                              >
                                                                  Facility Options
                                                              </InputLabel>
                                                              <Select
                                                                  labelId="facility-type-label"
                                                                  id="facility-type-select"
                                                                  multiple
                                                                  error={facilityTypeHasError}
                                                                  MenuProps={{
                                                                      variant: 'menu',
                                                                      getContentAnchorEl: null,
                                                                      anchorOrigin: {
                                                                          vertical: 'bottom',
                                                                          horizontal: 'left',
                                                                      },
                                                                      transformOrigin: {
                                                                          vertical: 'top',
                                                                          horizontal: 'left',
                                                                      },
                                                                  }}
                                                                  value={selectedFacilityTypeIds}
                                                                  onChange={(event) => {
                                                                      const value = event.target.value as number[];
                                                                      handleFacilityTypeChange(value);
                                                                  }}
                                                                  renderValue={(selected) => {
                                                                      const selectedArray = selected as number[];
                                                                      if (selectedArray.length === 0) {
                                                                          return <em>Select facilities</em>;
                                                                      }
                                                                      if (selectedArray.length === 1) {
                                                                          const facility = facilityTypes.find(f => f.facilityTypeId === selectedArray[0]);
                                                                          return facility?.facilityTypeName || '';
                                                                      }
                                                                      return `${selectedArray.length} facilities selected`;
                                                                  }}
                                                              >
                                                                  {facilityTypes.map((facilityType) => (
                                                                      <MenuItem 
                                                                          key={facilityType.facilityTypeId} 
                                                                          value={facilityType.facilityTypeId}
                                                                          dense
                                                                          className={classes.facilityTypeMenuItem}
                                                                      >
                                                                          <Checkbox 
                                                                              size="small"
                                                                              checked={selectedFacilityTypeIds.indexOf(facilityType.facilityTypeId) > -1}
                                                                          />
                                                                          <ListItemText 
                                                                              primary={facilityType.facilityTypeName} 
                                                                              className={classes.facilityTypeItemText}
                                                                          />
                                                                      </MenuItem>
                                                                  ))}
                                                              </Select>
                                                              {facilityTypeHasError && (
                                                                  <FormHelperText className={classes.helperTextAbsolute}>Required</FormHelperText>
                                                              )}
                                                          </FormControl>
                                                      </Grid>
                                                  )}
                                                  {showDropdown && (
                                                      <Grid item xs={3}>
                                                          <Box className={classes.numberOfDaysContainer}>
                                                              <Typography component="span" className={showMedicalCanvassNumberOfFacilitiesError ? classes.numberOfDaysLabel + ' ' + classes.numberOfDaysLabelError : classes.numberOfDaysLabel}>
                                                                  Number of Facilities
                                                              </Typography>
                                                              <FormControl error={showMedicalCanvassNumberOfFacilitiesError} className={classes.numberOfDaysInput}>
                                                                  <Input
                                                                      type="number"
                                                                      id={`medical-canvass-number-of-facilities-${servicetype.serviceTypeId}`}
                                                                      value={medicalCanvassNumberOfFacilities ?? ''}
                                                                      onKeyDown={(event) => {
                                                                          const allowedControlKeys = [
                                                                              'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
                                                                              'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End', 'Enter', 'Escape',
                                                                          ];
                                                                          if (event.ctrlKey || event.metaKey) return;
                                                                          if (allowedControlKeys.includes(event.key)) return;
                                                                          const blockedKeys = ['e', 'E', '+', '-', '.', ','];
                                                                          if (blockedKeys.includes(event.key) || !/^\d$/.test(event.key)) {
                                                                              event.preventDefault();
                                                                          }
                                                                      }}
                                                                      onPaste={(event) => {
                                                                          const pastedText = event.clipboardData.getData('text');
                                                                          if (!/^\d+$/.test(pastedText)) {
                                                                              event.preventDefault();
                                                                          }
                                                                      }}
                                                                      onChange={(event) => {
                                                                          const raw = event.target.value;
                                                                          const parsed = raw === '' ? null : Number.parseInt(raw, 10);
                                                                          handleSiuNumberOfDaysChange?.(MEDICAL_CANVASS_SERVICE_TYPE_ID, parsed);
                                                                      }}
                                                                      inputProps={{ min: 10 }}
                                                                  />
                                                                  {showMedicalCanvassNumberOfFacilitiesError && (
                                                                      <FormHelperText className={classes.numberOfDaysHelperText}>Minimum 10</FormHelperText>
                                                                  )}
                                                              </FormControl>
                                                          </Box>
                                                      </Grid>
                                                  )}
                                                  {showBackgroundInvestigationDropdown && (
                                                      <Grid item xs={4}>
                                                          <FormControl
                                                              fullWidth
                                                              required
                                                              error={showBackgroundInvestigationOptionError}
                                                              size="small"
                                                              className={classes.formControlRelative}
                                                          >
                                                              <InputLabel
                                                                  id={`bg-inv-option-label-${servicetype.serviceTypeId}`}
                                                                  className={classes.selectOptionLabel}
                                                                  error={showBackgroundInvestigationOptionError}
                                                              >
                                                                  Select an option
                                                              </InputLabel>
                                                              <Select
                                                                  labelId={`bg-inv-option-label-${servicetype.serviceTypeId}`}
                                                                  value={backgroundInvestigationOption ?? ''}
                                                                  error={showBackgroundInvestigationOptionError}
                                                                  onChange={(event) => {
                                                                      handleSiuOptionChange?.(
                                                                          BACKGROUND_INVESTIGATION_SERVICE_TYPE_ID,
                                                                          (event.target.value as string) || null,
                                                                      );
                                                                  }}
                                                                  MenuProps={{
                                                                      variant: 'menu',
                                                                      getContentAnchorEl: null,
                                                                      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                                                      transformOrigin: { vertical: 'top', horizontal: 'left' },
                                                                  }}
                                                              >
                                                                  {BACKGROUND_INVESTIGATION_OPTIONS.map((opt) => (
                                                                      <MenuItem key={opt} value={opt} dense className={classes.facilityTypeMenuItem}>
                                                                          <ListItemText primary={opt} className={classes.facilityTypeItemText} />
                                                                      </MenuItem>
                                                                  ))}
                                                              </Select>
                                                              {showBackgroundInvestigationOptionError && (
                                                                  <FormHelperText className={classes.helperTextAbsolute}>Required</FormHelperText>
                                                              )}
                                                          </FormControl>
                                                      </Grid>
                                                  )}
                                                  {showERemoteOptionDropdown && (
                                                      <Grid item xs={4}>
                                                          <FormControl
                                                              fullWidth
                                                              required
                                                              error={showERemoteOptionError}
                                                              size="small"
                                                              className={classes.formControlRelative}
                                                          >
                                                              <InputLabel
                                                                  id={`eremote-option-label-${servicetype.serviceTypeId}`}
                                                                  className={classes.selectOptionLabel}
                                                                  error={showERemoteOptionError}
                                                              >
                                                                  Select an option
                                                              </InputLabel>
                                                              <Select
                                                                  labelId={`eremote-option-label-${servicetype.serviceTypeId}`}
                                                                  value={eRemoteUnmannedOption ?? ''}
                                                                  error={showERemoteOptionError}
                                                                  onChange={(event) => {
                                                                      handleSiuOptionChange?.(
                                                                          EREMOTE_UNMANNED_SERVICE_TYPE_ID,
                                                                          (event.target.value as string) || null,
                                                                      );
                                                                  }}
                                                                  MenuProps={{
                                                                      variant: 'menu',
                                                                      getContentAnchorEl: null,
                                                                      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                                                      transformOrigin: { vertical: 'top', horizontal: 'left' },
                                                                  }}
                                                              >
                                                                  {EREMOTE_OPTIONS.map((opt) => (
                                                                      <MenuItem key={opt} value={opt} dense className={classes.facilityTypeMenuItem}>
                                                                          <ListItemText primary={opt} className={classes.facilityTypeItemText} />
                                                                      </MenuItem>
                                                                  ))}
                                                              </Select>
                                                              {showERemoteOptionError && (
                                                                  <FormHelperText className={classes.helperTextAbsolute}>Required</FormHelperText>
                                                              )}
                                                          </FormControl>
                                                      </Grid>
                                                  )}
                                                  {showNumberOfDaysInput && (
                                                      <Grid item xs={3}>
                                                          <Box className={classes.numberOfDaysContainer}>
                                                              <Typography component="span" className={numberOfDaysLabelClassName}>
                                                                  Number of Days
                                                              </Typography>
                                                              <FormControl error={showNumberOfDaysError} className={classes.numberOfDaysInput}>
                                                                  <Input
                                                                      type="number"
                                                                      id={`siu-number-of-days-${servicetype.serviceTypeId}`}
                                                                      value={numberOfDaysValue ?? ''}
                                                                      onKeyDown={(event) => {
                                                                          const allowedControlKeys = [
                                                                              'Backspace',
                                                                              'Delete',
                                                                              'ArrowLeft',
                                                                              'ArrowRight',
                                                                              'ArrowUp',
                                                                              'ArrowDown',
                                                                              'Tab',
                                                                              'Home',
                                                                              'End',
                                                                              'Enter',
                                                                              'Escape',
                                                                          ];

                                                                          if (event.ctrlKey || event.metaKey) {
                                                                              return;
                                                                          }

                                                                          if (allowedControlKeys.includes(event.key)) {
                                                                              return;
                                                                          }

                                                                          const blockedKeys = ['e', 'E', '+', '-', '.', ','];
                                                                          if (blockedKeys.includes(event.key) || !/^\d$/.test(event.key)) {
                                                                              event.preventDefault();
                                                                          }
                                                                      }}
                                                                      onPaste={(event) => {
                                                                          const pastedText = event.clipboardData.getData('text');
                                                                          if (!/^\d+$/.test(pastedText)) {
                                                                              event.preventDefault();
                                                                          }
                                                                      }}
                                                                      onChange={(event) => {
                                                                          const rawValue = event.target.value;
                                                                          if (rawValue === '') {
                                                                              handleSiuNumberOfDaysChange?.(servicetype.serviceTypeId, null);
                                                                              return;
                                                                          }
                                                                          if (!/^\d+$/.test(rawValue)) {
                                                                              return;
                                                                          }
                                                                          const parsedValue = parseInt(rawValue, 10);
                                                                          handleSiuNumberOfDaysChange?.(servicetype.serviceTypeId, parsedValue);
                                                                      }}
                                                                      inputProps={{
                                                                          min: numberOfDaysMin,
                                                                          step: 1,
                                                                          inputMode: 'numeric',
                                                                          pattern: '[0-9]*',
                                                                          autoComplete: 'off',
                                                                      }}
                                                                      disableUnderline={false}
                                                                  />
                                                                  {showNumberOfDaysError && (
                                                                      <FormHelperText className={classes.numberOfDaysHelperText}>{`Minimum ${numberOfDaysMin}`}</FormHelperText>
                                                                  )}
                                                              </FormControl>
                                                          </Box>
                                                      </Grid>
                                                  )}
                                              </Grid>
                                          </ListItem>
                                      );
                                  })
                                : sortedServiceTypes.map((servicetype, sindex) => (
                                      <ListItem key={servicetype.serviceTypeId} className={classes.nested}>
                                          <ListItemText>
                                              <FormControlLabel
                                                  control={
                                                      <Radio
                                                          className={classes.radioButton}
                                                          checked={
                                                              serviceTypesSelected.findIndex(
                                                                  (sts) => sts === servicetype.serviceTypeId,
                                                              ) !== -1
                                                          }
                                                          onChange={(event) =>
                                                              myHandleServiceTypeSelected(
                                                                  +(event.target as HTMLInputElement).value,
                                                                  service.service.serviceId,
                                                              )
                                                          }
                                                          value={servicetype.serviceTypeId}
                                                      />
                                                  }
                                                  label={
                                                      <span className={classes.radioButtonLabel}>
                                                          {servicetype.serviceTypeName}
                                                      </span>
                                                  }
                                              />
                                          </ListItemText>
                                      </ListItem>
                                  ))}
                        </List>
                    </Collapse>
                </>
            ) : (
                ''
            )}
        </>
    );
};